/**
 * DEMO-ONLY: sessionStorage-backed ICM `quickorder` REST for Presidio LOCAL_MOCK.
 * Removable with mock.interceptor.presidio.ts wiring. Production uses real ICM.
 */
import { resolvePresidioProductSku } from './presidio-mpn-sku-map';
import { PRESIDIO_MOCK_PRODUCT_PRICES } from './presidio-mock-product-prices';
import { isDemoPriceOnRequestSku } from './presidio-price-on-request-skus';

export const PRESIDIO_MOCK_QUICKORDER_KEY = 'presidio-mock-quickorder';

export interface PresidioMockQuickOrderItem {
  position: number;
  itemLineId: string;
  productSKU: string;
  productName: string;
  quantity: number;
  eachPrice: { type: 'Money'; value: number; currency: string };
  totalPrice: { type: 'Money'; value: number; currency: string };
  selected?: boolean;
}

export interface PresidioMockQuickOrder {
  wishlistId: string;
  userId: string;
  customerNo?: string;
  noOfItems: number;
  quickOrderTotal: { type: 'Money'; value: number; currency: string };
  items: PresidioMockQuickOrderItem[];
  errors?: { code: string; message: string; status: number }[];
}

export interface PresidioMockUpdateQuickOrderItem {
  position?: number;
  productSKU: string;
  quantity: number;
  quantityUpdate?: boolean;
}

/** Display names for seeded demo SKUs (no PII). */
const DEMO_SKU_NAMES: Record<string, string> = {
  'CISCO-C9300': 'Cisco Catalyst 9300',
  'C9200-48P': 'Cisco Catalyst 9200',
  'PRS-DELL-R760': 'Dell PowerEdge R760',
  'DCS-7050X3': 'Arista 7050X',
  'NV-L40S-48G': 'NVIDIA L40S',
  'PRS-EDGE-AP': 'Edge Access Point',
  'PRS-NAS-48': 'Presidio NAS Storage',
  'DELL-AI-STOR-60': 'AI Storage 60TB',
  'CT8G4DFRA32': '8GB DDR4 SODIMM',
  'CT16G56C46S5': '16GB DDR5 SODIMM',
  'M321R4GA3BB0': '32GB DDR5 RDIMM',
  'M321R8GA0BB0': '64GB DDR5 RDIMM',
  'KSM56R46BD4': '128GB DDR5 Memory Kit',
};

function money(value: number): { type: 'Money'; value: number; currency: string } {
  return { type: 'Money', value: Math.round(value * 100) / 100, currency: 'USD' };
}

function emptyQuickOrder(): PresidioMockQuickOrder {
  return {
    wishlistId: 'MOCK-QO-001',
    userId: 'demo-user',
    customerNo: 'PRESIDIO-MOCK',
    noOfItems: 0,
    quickOrderTotal: money(0),
    items: [],
    errors: [],
  };
}

function isKnownSku(sku: string): boolean {
  const resolved = resolvePresidioProductSku(sku);
  if (!resolved) {
    return false;
  }
  return (
    Object.prototype.hasOwnProperty.call(PRESIDIO_MOCK_PRODUCT_PRICES, resolved) ||
    Object.prototype.hasOwnProperty.call(DEMO_SKU_NAMES, resolved) ||
    isDemoPriceOnRequestSku(resolved)
  );
}

/** Accept ICM sku or manufacturer part #; persist catalog sku (=== manufacturer SKU). */
function resolveSku(sku: string): string {
  return resolvePresidioProductSku(sku);
}

function productName(sku: string): string {
  return DEMO_SKU_NAMES[sku] || sku;
}

function unitPrice(sku: string): number {
  if (isDemoPriceOnRequestSku(sku)) {
    return 0;
  }
  return PRESIDIO_MOCK_PRODUCT_PRICES[sku] ?? 100;
}

function recompute(qo: PresidioMockQuickOrder): PresidioMockQuickOrder {
  const items = qo.items.map((item, i) => {
    const each = unitPrice(item.productSKU);
    return {
      ...item,
      position: i + 1,
      eachPrice: money(each),
      totalPrice: money(each * item.quantity),
    };
  });
  const total = items.reduce((sum, it) => sum + it.totalPrice.value, 0);
  return {
    ...qo,
    items,
    noOfItems: items.length,
    quickOrderTotal: money(total),
  };
}

function read(): PresidioMockQuickOrder {
  if (typeof sessionStorage === 'undefined') {
    return emptyQuickOrder();
  }
  try {
    const raw = sessionStorage.getItem(PRESIDIO_MOCK_QUICKORDER_KEY);
    if (!raw) {
      const seed = emptyQuickOrder();
      write(seed);
      return seed;
    }
    return recompute(JSON.parse(raw) as PresidioMockQuickOrder);
  } catch {
    return emptyQuickOrder();
  }
}

function write(qo: PresidioMockQuickOrder): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  sessionStorage.setItem(PRESIDIO_MOCK_QUICKORDER_KEY, JSON.stringify(recompute(qo)));
}

export function getPresidioMockQuickOrder(): PresidioMockQuickOrder {
  return read();
}

export function createPresidioMockQuickOrderItems(
  incoming: PresidioMockUpdateQuickOrderItem[]
): PresidioMockQuickOrder {
  const qo = read();
  const errors: PresidioMockQuickOrder['errors'] = [];
  let nextId = qo.items.length + 1;

  for (const row of incoming || []) {
    const sku = resolveSku(row.productSKU);
    const qty = Number(row.quantity);
    if (!sku || !qty || qty <= 0 || Number.isNaN(qty)) {
      continue;
    }
    if (!isKnownSku(sku)) {
      errors.push({
        code: 'invalid.product',
        message: `Part # ${sku} is invalid.`,
        status: 400,
      });
      continue;
    }
    const existing = qo.items.find(i => i.productSKU === sku);
    if (existing) {
      existing.quantity = row.quantityUpdate ? qty : existing.quantity + qty;
    } else {
      const each = unitPrice(sku);
      qo.items.push({
        position: nextId,
        itemLineId: `qo-line-${nextId}`,
        productSKU: sku,
        productName: productName(sku),
        quantity: qty,
        eachPrice: money(each),
        totalPrice: money(each * qty),
        selected: false,
      });
      nextId += 1;
    }
  }

  const result = recompute({ ...qo, errors });
  write(result);
  return result;
}

export function updatePresidioMockQuickOrderItem(
  itemId: string,
  patch: PresidioMockUpdateQuickOrderItem
): PresidioMockQuickOrder {
  const qo = read();
  const item = qo.items.find(i => i.itemLineId === itemId);
  if (!item) {
    return recompute({ ...qo, errors: [{ code: 'not.found', message: 'Line not found', status: 404 }] });
  }
  if (patch.productSKU) {
    const sku = resolveSku(patch.productSKU);
    if (!isKnownSku(sku)) {
      const result = recompute({
        ...qo,
        errors: [{ code: 'invalid.product', message: `Part # ${sku} is invalid.`, status: 400 }],
      });
      write(result);
      return result;
    }
    item.productSKU = sku;
    item.productName = productName(sku);
  }
  if (patch.quantity !== undefined && !Number.isNaN(Number(patch.quantity))) {
    item.quantity = Number(patch.quantity);
  }
  const result = recompute({ ...qo, errors: [] });
  write(result);
  return result;
}

export function deletePresidioMockQuickOrderItems(itemLineIds: string[]): PresidioMockQuickOrder {
  const qo = read();
  const ids = new Set(itemLineIds || []);
  qo.items = qo.items.filter(i => !ids.has(i.itemLineId));
  const result = recompute({ ...qo, errors: [] });
  write(result);
  return result;
}

export function resetPresidioMockQuickOrder(): void {
  write(emptyQuickOrder());
}
